<template>
  <div>
    <!-- mobile view -->
    <div class="pb-6 md:hidden">
      <template v-if="!selectedFolderId || isRouteFolderDetails || isShareRouteFolderDetails">
        <div class="flex items-center justify-between" v-if="folders && folders.length !== 0">
          <h3 class="font-bold">
            {{ $i18nPresenter.t("Folders") }}
          </h3>

          <sort-by simplified />
        </div>

        <div class="flex overflow-x-auto flex-nowrap">
          <div
            class="flex-shrink-0 w-40 p-3 mt-4 mb-4 ml-4 rounded shadow-card bg-base-0"
            :key="`folder-${folder.folderId}`"
            v-for="folder in folders"
          >
            <div class="flex justify-between">
              <aha-icon-container
                class="my-3 font-bold md:truncate cursor-pointer w-12 relative"
                size="lg"
                icon-class="bg-purple-10 text-purple-50"
                variant="square"
              >
                <aha-icon name="folder" />
                <aha-icon
                  v-if="showIconShared(folder)"
                  name="users"
                  width="20"
                  height="20"
                  viewBox="-4 -4 20 20"
                  class="absolute -top-1.5 -left-1.5 rounded-full border-2 border-purple-10 bg-white-100 text-base-70"
                />
              </aha-icon-container>
              <folder-kebab-menu :folder="folder" />
            </div>
            <div
              :title="folder.folderName"
              class="my-3 font-bold truncate cursor-pointer hover:text-primary-90"
            >
              {{ folder.folderName }}
            </div>
            <div class="flex items-center">
              <aha-icon-container>
                <aha-icon
                  name="file"
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                />
              </aha-icon-container>
            </div>
          </div>
        </div>
      </template>

      <div class="flex items-center justify-between" v-if="presentations && presentations.length !== 0">
        <h3 class="font-bold">
          {{ $i18nPresenter.t("Presentations") }}
        </h3>
        <sort-by simplified v-if="folders.length === 0 || selectedFolderId" />
      </div>

      <div
        :key="`presentation-${presentation.id}`"
        v-for="presentation in presentations"
        class="flex p-3 my-4 rounded shadow-card bg-base-0"
      >
        <span>
          <presentation-thumbnail
            :showIconShared="false"
            :presentation="presentation"
          />
        </span>
        <span class="flex flex-col justify-between flex-grow w-0 ml-4">
          <div
            class="font-bold truncate cursor-pointer"
            :title="presentation.name"
          >
            <span
              class="text-base-60 text-link"
              v-if="!isSharedPresentation(presentation)"
            >
              {{ getFolderName(presentation) }}</span>
            <span class="text-link">{{ presentation.name }}</span>
          </div>
          <div class="flex justify-between overflow-hidden max-h-6">
            <span class="flex flex-wrap items-center leading-6">
              <span class="hidden min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="slides"
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  {{ presentation.slideCount }}
                </span>
              </span>

              <span class="hidden ml-4 min-w-max 2xs:inline">
                <aha-icon-container>
                  <aha-icon
                    name="people"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
                <span class="text-xs text-base-80">
                  <aha-formatter
                    :value="presentation.participantsCount || presentation.onlineCount"
                    :locale="presentation.language"
                    type="number"
                  />
                </span>
              </span>
            </span>
          </div>
        </span>
        <presentation-kebab-menu
          v-if="isShowPresentationKebabMenu"
          class="flex items-center"
          :presentation="presentation"
          :right-click-enabled="false"
        >
          <template #context-menu-content>
            <aha-context-menu-item
              v-aha-emit-action="{ presentation_id: presentation.id }"
              @click="handleClickRestore(presentation)"
              class="context-menu--present"
            >
              <aha-spinner v-if="isProcessing" class="mr-2" />
              <aha-icon
                v-else
                name="restore"
                width="16"
                viewBox="0 0 12 12"
              />
              <span>{{ $i18nPresenter.t("Restore") }}</span>
            </aha-context-menu-item>
          </template>
        </presentation-kebab-menu>
      </div>
    </div>

    <!-- desktop view -->
    <table class="hidden main-table md:table">
      <thead>
        <tr>
          <table-heading name="thumbnail">
            <span
              class="inline-flex items-center cursor-pointer"
              @click="handleClickColumnHeader('name')"
            >
              <span class="mr-2">{{ $i18nPresenter.t("Name") }}</span>
              <sorting-indicator v-if="sortColumn === 'name'" :order="sortOrder" />
            </span>
          </table-heading>
          <table-heading name="name" />
          <table-heading name="accessCode">
            {{ $i18nPresenter.t("Access Code") }}
          </table-heading>
          <table-heading name="delete">
            <span
              class="inline-flex items-center cursor-pointer"
              @click="handleClickColumnHeader('deletedAt')"
            >
              <span class="mr-2">{{ $i18nPresenter.t("Deleted") }}</span>
              <sorting-indicator v-if="sortColumn === 'deletedAt'" :order="sortOrder" />
            </span>
          </table-heading>
          <!-- <table-heading name="days-left">
            <span
              class="inline-flex items-center cursor-pointer"
            >
              <span class="mr-2">{{ $i18nPresenter.t("Days left") }}</span>
            </span>
          </table-heading> -->
          <table-heading class="max-w-48 w-48" name="kebabMenu" />
        </tr>
      </thead>

      <tbody>
        <!-- desktop folder view -->
        <template v-if="!selectedFolderId || isRouteFolderDetails || isShareRouteFolderDetails">
          <tr
            :key="`folder-${folder.folderId}`"
            v-for="folder in folders"
            @contextmenu.prevent="openKebabMenuFolder(folder, $event)"
            :class="{ 'team-folder': folder.accountId && !folder.parentFolderId }"
          >
            <table-data name="thumbnail" class="main-table--thumbnail">
              <span
                class="flex justify-center cursor-pointer"
              >
                <span class="w-20 text-center h-11">
                  <aha-icon-container
                    size="auto"
                    :margin-right="false"
                    icon-class="bg-purple-10 text-purple-50 relative"
                    variant="square"
                  >
                    <aha-icon name="folder" />
                    <aha-icon
                      v-if="showIconShared(folder)"
                      name="users"
                      width="20"
                      height="20"
                      viewBox="-4 -4 20 20"
                      class="absolute -top-1.5 -left-1.5 rounded-full border-2
                        border-purple-10 bg-white-100 text-base-70"
                    />
                  </aha-icon-container>
                </span>
              </span>
            </table-data>
            <table-data
              name="name"
              class="main-table--item-name text-link"
            >
              <a
                :title="folder.folderName"
                class="text-link"
              >
                {{ folder.folderName }}
              </a>
              <span class="flex flex-wrap items-center mt-1 font-normal leading-6">
                <aha-icon-container>
                  <aha-icon
                    name="file"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                  />
                </aha-icon-container>
              </span>
            </table-data>
            <table-data name="accessCode" class="lg:w-32" />
            <table-data name="kebabMenuFolder" class="text-center lg:w-10 rounded-r-lg border-r">
              <folder-kebab-menu
                v-if="showDesktopFolderKebabMenu"
                :folder="folder"
              />
            </table-data>
          </tr>
        </template>

        <!-- desktop presentations list view -->
        <tr
          :key="`presentation-${presentation.id}`"
          v-for="(presentation) in presentations"
          class="table-row"
          @contextmenu.prevent="openKebabMenuPresentation(presentation, $event)"
          :class="{ 'active': presentation.selected }"
        >
          <table-data
            name="thumbnail"
            class="main-table--thumbnail main-table--table-cell"
          >
            <presentation-thumbnail
              :showIconShared="false"
              :presentation="presentation"
            />
          </table-data>
          <table-data
            name="name"
            class="main-table--item-name text-link main-table--table-cell min-w-44"
          >
            <PresentationName
              :allowRedirectToReportPage="false"
              :showInfo="false"
              :presentation="presentation"
              :folder-name="getFolderName(presentation)"
              :is-shared-presentation="isSharedPresentation(presentation)"
            />
          </table-data>
          <table-data
            name="accessCode"
            class="text-xs lg:w-32 main-table--table-cell"
          >
            {{ presentation.accessCode }}
          </table-data>
          <table-data
            name="deletedAt"
            class="text-xs cursor-pointer lg:w-28 main-table--table-cell"
          >
            <aha-formatter
              :value="presentation.deletedAt"
              type="date"
              showTooltip
              :tooltipValue="presentation.deletedAt | dateTime"
              :locale="locale"
            />
          </table-data>
          <!-- <table-data
            name="daysLeft"
            class="text-xs cursor-pointer lg:w-28 main-table--table-cell"
          >
            {{ getRemainingTime(presentation.deletedAt) }}
          </table-data> -->
          <table-data
            name="kebabMenuPresentation"
            class="text-center lg:w-10 main-table--table-cell"
          >
            <aha-antd-button
              class="restore-button opacity-0 mr-2 whitespace-nowrap"
              variant="secondary"
              name="restore-button"
              icon-name="restore"
              icon-view-box="0 0 12 12"
              :submitting="isProcessing"
              v-aha-emit-action="{ presentation_id: presentation.id }"
              @click="handleClickRestore(presentation)"
            >
              {{ $i18nPresenter.t('Restore') }}
            </aha-antd-button>
          </table-data>
        </tr>
      </tbody>
    </table>

    <div class="text-center" v-if="morePresentationsLoading">
      <aha-spinner />
    </div>

    <modal-view-only-warning
      @hide="handleHideViewOnlyWarningModal"
      @view="handleClickView"
      v-if="isShowingViewOnlyWarningModal"
      :active-editor="activeEditor"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { truncate } from 'lodash'
import EventBus from '@/eventBus'

import constants from '@/constant'
import ModalViewOnlyWarning from '@/components-v2/modals/ViewOnlyWarning.vue'
import PresentationName from '@/components-v2/my-presentations/main-table/PresentationName.vue'
import FolderKebabMenu from './FolderKebabMenu.vue'
import PresentationThumbnail from './PresentationThumbnail.vue'
import PresentationKebabMenu from './PresentationKebabMenu.vue'
import SortBy from '../SortBy.vue'
import SortingIndicator from './SortingIndicator.vue'
import TableData from './TableData.vue'
import TableHeading from './TableHeading.vue'
import { getRatingTooltip } from '@/vuex/modules/presentation/presentation-report.module'

// const RETENTION_PERIOD = 30

export default {
  components: {
    FolderKebabMenu,
    PresentationKebabMenu,
    PresentationName,
    PresentationThumbnail,
    SortBy,
    SortingIndicator,
    TableData,
    TableHeading,
    ModalViewOnlyWarning,
  },
  props: {
    folders: {
      type: Array,
      default: () => [], // Default to an empty array
    },
    morePresentationsLoading: {
      type: Boolean,
      default: false, // Default to false
    },
    selectedFolderId: {
      type: [String, Number],
      default: null, // Default to null if no folder is selected
    },
    presentations: {
      type: Array,
      default: () => [], // Default to an empty array
    },
    sortColumn: {
      type: String,
      default: '', // Default to an empty string
    },
    sortOrder: {
      type: String,
      default: '', // Default to an empty string
    },
    currentUser: {
      type: Object,
      default: () => ({}), // Default to an empty object
    },
    locale: {
      type: String,
      default: 'en', // Default to 'en' (English) or any other default locale
    },
    workingMode: {
      type: String,
      default: 'normal', // Default to 'normal' or another working mode
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      areLoadingDefaultPresentations: false,
      presentationsAndFoldersLoading: false,
      isShowingViewOnlyWarningModal: false,
      isClaimingFreeOneTimeOffer: false,
      isDecliningFreeOneTimeOffer: false,
      isShowingWrapup: true,
      activePresentation: null,
      activeEditor: null,
      permissions: constants.permissions,
      isAllPresentationsSelected: false,
      isShowTooltipTeamFolder: false,
      isShowTooltipTeamManagerment: false,
      loadingAddPresentationToPPT: null,
    }
  },
  computed: {
    isShowCheckAllPresentations() {
      return true
    },
    isShowPresentationKebabMenu() {
      return true
    },
    isShowPresentButton() {
      return truncate
    },

    isShowDesktopPresentationKebabMenu() {
      return true
    },
    showDesktopFolderKebabMenu() {
      return true
    },
    isRoutePresentations() {
      return true
    },
    isRouteFolderDetails() {
      return true
    },
    isShareRouteFolderDetails() {
      return true
    },
  },
  methods: {
    ...mapActions('presentationReport', ['showPresentationReportAction']),
    ...mapActions([
      'setPresentationsAction',
    ]),

    getRatingTooltip,
    handleHideViewOnlyWarningModal() {
      this.isShowingViewOnlyWarningModal = null
    },
    closeOtherMenu() {
      document.body.click()
    },
    openKebabMenuPresentation(presentation, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit('open-kebab-presentation', { x, y, id: presentation.id })
    },
    openKebabMenuFolder(folder, event) {
      this.closeOtherMenu()
      const { x, y } = event
      EventBus.$emit(`open-kebab-folder-${folder.folderId}`, { x, y, id: folder.folderId })
    },

    // TODO: Ideally, we can config every items inside the kebab menu
    handleClickRestore(presentation) {
      this.$emit('click-restore', presentation)
    },
    getFolderName(presentation) {
      const { q: searchValue } = this.$route.query

      if (!searchValue) return null

      if (!presentation.folder) return null

      return `${presentation.folder.folderName} / `
    },
    handleAllPresentationSelect(value) {
      this.isAllPresentationsSelected = value
      const newPresentations = this.presentations.map((p) => {
        if (p.userId === this.currentUser.id && p.permissions.includes(constants.permissions.DELETE_FOLDER)) {
          return { ...p, selected: value }
        }
        return p
      })
      this.setPresentationsAction(newPresentations)
    },
    handleClickColumnHeader(name) {
      this.$emit('click-column-header', name)
    },
    async updateAccessCode(updatedItem) {
      const newPresentations = this.presentations.map((item) => {
        if (item.id === updatedItem.id) {
          return { ...item, accessCode: updatedItem.newAccessCode.toUpperCase() }
        }

        return item
      })
      this.setPresentationsAction(newPresentations)
    },
    isSharedPresentation(presentation) {
      return presentation.userId !== this.currentUser.id
    },
    isShowCheckBoxForPresentation(presentation) {
      if (presentation.userId === this.currentUser.id &&
        presentation.permissions && presentation.permissions.includes(constants.permissions.DELETE_FOLDER)) {
        return true
      }
      return false
    },
    showIconShared(folder) {
      if (this.isShareRouteFolderDetails) {
        return true
      }
      if (this.isRouteFolderDetails && this.$store.state.parentFolderHasShared) {
        return true
      }
      return folder.countShareIndividual > 0 || folder.countShareTeam > 0
    },
    // getRemainingTime(deletedAt) {
    //   let days = ''
    //   if (!deletedAt) return days
    //   // Convert deletedAt string to a Date object
    //   const deletedDate = new Date(deletedAt)
    //   // Calculate the retention date (30 days after deletedAt)
    //   const retentionDate = new Date(deletedDate)
    //   retentionDate.setDate(deletedDate.getDate() + RETENTION_PERIOD)
    //   // Get the current date
    //   const now = new Date()

    //   // Calculate the difference in milliseconds
    //   const timeDiff = retentionDate - now

    //   // Convert the time difference into days
    //   days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
    //   return this.$i18nPresenter.t('{days} days', { days })
    // },

  },
}
</script>

<style lang="scss">
.main-table {
  @apply border-separate w-full;
  border-spacing: 0 1rem;
}

.main-table tbody tr {
  @apply rounded-lg text-base-80 shadow-card h-12;

  &:hover {
    box-shadow: 0px 25px 20px -20px rgba(0, 0, 0, .2);
    .report-button {
      @media screen and (min-width:1100px) {
        @apply opacity-100;
      }
    }
    .restore-button {
      opacity: 100;
    }
  }

  &.active {
    @apply ring-1;

    &:hover {
      @apply ring-1;
    }
  }

  transition: .5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
}

.main-table td.main-table--table-cell:first-child {
  @apply rounded-l-lg border-l;
}

.main-table td.main-table--table-cell:last-child {
  @apply rounded-r-lg border-r;
}

.main-table--thumbnail {
  @apply w-20 relative;
}

.main-table--item-name {
  @apply ml-4 font-bold truncate transition-colors max-w-48;
  &:hover > a {
    @apply text-primary-100;
  }
}

.published-icon {
  position: relative;
  top: 0rem;
  left: 0.25rem;
}

.published-badge {
  color: white;
  background: linear-gradient(to bottom right,#33C173 50%,white 50%);
  border-radius: 0.5rem 0rem;
  position: absolute;
  width: 2.1rem;
  height: 2.1rem;
  top: -1px;
  left: -1px;
}
</style>
